import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { RootState } from 'state/store';

import { getRoommateInterests } from 'services';
import { RoommateInterest } from 'types/roommate-interest';

import { useLocale } from './utils/useLocale';

interface UseRoommateInterestsOptions {
  propertyId?: number;
  enabled?: boolean;
  staleTime?: number;
  retry?: number;
  onError?: (error: Error) => void | undefined;
}

interface UseRoommateInterestsReturn extends Omit<UseQueryResult<RoommateInterest[]>, 'data'> {
  questions: RoommateInterest[];
  refetchInterests: () => void;
}

export const useRoommateInterests = ({
  propertyId,
  enabled = true,
  retry = 2,
  onError,
}: UseRoommateInterestsOptions = {}): UseRoommateInterestsReturn => {
  const locale = useLocale();

  const { isPreviewMode = false } = useSelector((state: RootState) => state.auth);

  // Get propertyId from Redux state if not provided
  const currentPropertyId = useSelector(
    (state: RootState) => propertyId || state.application.applicant.propertyId,
  );

  const fetchRoommateInterests = useCallback(async (): Promise<RoommateInterest[]> => {
    if (!currentPropertyId) {
      throw new Error('Property ID is required to fetch roommate interests');
    }

    const response = await getRoommateInterests(currentPropertyId);
    return response || [];
  }, [currentPropertyId]);

  const queryResult = useQuery({
    queryKey: ['roommateInterests', currentPropertyId, locale],
    queryFn: fetchRoommateInterests,
    enabled: enabled && !!currentPropertyId && !isPreviewMode,
    retry,
    ...(onError && { onError }),
  });

  return {
    ...queryResult,
    questions: isPreviewMode ? [] : (queryResult.data ?? []),
    refetchInterests: queryResult.refetch,
  };
};
